import { useFormikContext } from 'formik';
import React, { useContext, useEffect } from 'react';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Title } from 'Components/frameworks/title/Title';
import { PaymentFees } from 'Components/widgets/payment-info/payment-method/PaymentFees';
import { t } from 'Src/i18n';
import { pluralizeLabel } from 'Utils/label/labelUtils';
import * as yup from 'yup';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { useAppContext } from 'Context/ReactAppContext';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { useBookingNavbarContext } from 'Components/widgets/booking-navbar/BookingNavbarContext';
import type { BookingNavbarContextExtended, PaymentFormValues } from 'Src/types/payment';
import { PaymentFormikInput } from 'Components/widgets/payment-info/paymentFormik';
type Props = {
  paymentProvider: PaymentProvider;
  requiredRule?: () => boolean;
};
export const PaymentMethodsWithConditions = ({
  paymentProvider,
  requiredRule = yup.string().required(),
}: Props) => {
  const formik = useFormikContext<PaymentFormValues>();
  const { locale } = useAppContext();
  const { currency, setCardFeesPercentage } = useContext(PaymentInfoContext);
  const { hideContinueButton } = useBookingNavbarContext() as BookingNavbarContextExtended;
  const { paymentConditionId } = formik.values;
  useEffect(() => {
    if (!formik.values.paymentMethodId) {
      formik.setFieldValue('paymentMethodId', paymentProvider.paymentMethods[0].id);
      formik.setFieldValue(
        'paymentConditionId',
        paymentProvider.paymentMethods[0].timePaymentConditions[0].id,
      );
    }
  }, [formik.values.paymentMethodId]);
  const availableTimePaymentConditions = paymentProvider.paymentMethods.reduce(
    (acc, { timePaymentConditions }) => [...acc, ...timePaymentConditions],
    [] as PaymentMethod['timePaymentConditions'][number][],
  );
  const paymentCountSelected = availableTimePaymentConditions.find(
    ({ id }) => id === paymentConditionId,
  )?.paymentCount;
  const paymentMethodSelected = paymentProvider.paymentMethods.find(
    ({ id }) => id === formik.values.paymentMethodId,
  );

  const computeInstallments = ({
    paymentCount,
    chargePercentage,
  }: {
    paymentCount: number;
    chargePercentage: number;
  }) => {
    const { amountToPay, freeDeposit } = formik.values;
    const amount = freeDeposit || amountToPay;
    const fees = amount * (chargePercentage / 100);
    const installment = (amount + fees) / paymentCount;
    return {
      amount: formatAmountForLocale(installment, locale, currency, {
        precision: 2,
      }),
      fees,
    };
  };

  useEffect(() => {
    if (paymentMethodSelected) {
      formik.setFieldValue(
        'paymentConditionId',
        paymentMethodSelected.timePaymentConditions.find(
          ({ paymentCount }) => paymentCount === paymentCountSelected,
        )?.id || paymentMethodSelected.timePaymentConditions[0].id,
      );
    }
  }, [paymentMethodSelected]);
  useEffect(() => {
    if (paymentMethodSelected && paymentConditionId && setCardFeesPercentage) {
      const selectedCondition = paymentMethodSelected.timePaymentConditions.find(
        ({ id }) => id === formik.values.paymentConditionId,
      );
      if (selectedCondition) {
        setCardFeesPercentage(selectedCondition.chargePercentage / 100);
      }
    }
  }, [paymentConditionId]);
  const isFormValid = formik.isValid && Object.values(formik.touched).length;
  const paymentConditionSelected = paymentMethodSelected?.timePaymentConditions?.find(
    ({ id }) => id === formik.values.paymentConditionId,
  );
  useEffect(() => {
    hideContinueButton &&
      isFormValid &&
      paymentConditionSelected &&
      setTimeout(formik.submitForm, 0);
  }, [formik.values.paymentConditionId, hideContinueButton]);

  if (!paymentConditionSelected) {
    return null;
  }

  return (
    <div className="mt-20">
      <Title className="mb-10" theme="title-brand-n2">
        {t('booking.banking.paymentMethod.title')}
      </Title>
      <Capsule header="none" contentClassName="p-20">
        <div className="-m-10 flex flex-wrap items-end">
          <div className="w-full p-10 md:w-1/2">
            <PaymentFormikInput
              type="select"
              name="paymentMethodId"
              label={t('booking.banking.paymentMethod.selectCardTypeLabel')}
              showValidateState={false}
              options={paymentProvider.paymentMethods.map(({ id, label }) => ({
                name: label,
                value: id,
              }))}
            />
          </div>

          <div className="w-full p-10 md:w-1/2">
            <PaymentFormikInput
              type="select"
              name="paymentConditionId"
              label={t('booking.banking.paymentMethod.selectNumberOfMonthsLabel')}
              showValidateState={false}
              options={paymentMethodSelected.timePaymentConditions.map(
                ({ id, paymentCount, chargePercentage }) => {
                  const { amount, fees } = computeInstallments({
                    paymentCount,
                    chargePercentage,
                  });
                  return {
                    name:
                      t(
                        pluralizeLabel(
                          'booking.banking.paymentMethod.monthLabel',
                          'booking.banking.paymentMethod.monthsLabel',
                          paymentCount,
                        ),
                        {
                          month_payment_count: paymentCount,
                          amount,
                        },
                      ) +
                      ' ' +
                      (fees
                        ? t('booking.banking.paymentMethod.withFees')
                        : t('booking.banking.paymentMethod.withoutFees')),
                    value: id,
                  };
                },
              )}
              validate={requiredRule}
              isRequired
            />
          </div>
        </div>

        {paymentConditionSelected.chargePercentage > 0 && (
          <PaymentFees chargePercentage={paymentConditionSelected.chargePercentage} />
        )}
      </Capsule>
    </div>
  );
};
